import { useEffect, useMemo, useState } from "react";
import { Plus, ShieldCheck, Lock, Trash2, Users, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const LEVEL_LABEL = { none: "None", view: "View", edit: "Edit", admin: "Admin" };
const LEVEL_STYLES = {
  none: "bg-slate-100 text-slate-500 border-slate-200",
  view: "bg-emerald-50 text-emerald-700 border-emerald-200",
  edit: "bg-amber-50 text-amber-700 border-amber-200",
  admin: "bg-indigo-50 text-indigo-700 border-indigo-200",
};
const MODULE_LABEL = {
  clients: "Clients",
  catalog: "Machine Catalog",
  inventory: "Inventory",
  leads: "Leads",
  bookings: "Demo Bookings",
  quotations: "Quotations",
  team: "Team / Users",
  roles: "Roles & Permissions",
};

function permsFromMeta(modules) {
  return modules.reduce((acc, m) => ({ ...acc, [m]: "none" }), {});
}

export default function Roles() {
  const { user: me } = useAuth();
  const [meta, setMeta] = useState({ modules: [], levels: [], system_admin_role_name: "Admin" });
  const [roles, setRoles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [draft, setDraft] = useState(null); // {name, description, permissions}
  const [createOpen, setCreateOpen] = useState(false);
  const canAdmin = me?.permissions?.roles === "admin";

  const load = async () => {
    try {
      const [mt, rs] = await Promise.all([api.get("/roles/meta"), api.get("/roles")]);
      setMeta(mt.data);
      setRoles(rs.data);
      if (!selectedId && rs.data.length) setSelectedId(rs.data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (me) load(); }, [me]);

  const selected = useMemo(() => roles.find((r) => r.id === selectedId) || null, [roles, selectedId]);
  useEffect(() => {
    if (selected) { setDraft({ name: selected.name, description: selected.description || "", permissions: { ...selected.permissions } }); setDirty(false); }
  }, [selected]);

  if (me && me.permissions && me.permissions.roles === "none") return <Navigate to="/dashboard" replace />;

  const isAdminRole = selected?.name === meta.system_admin_role_name;
  const permsLocked = !canAdmin || isAdminRole;
  const namingLocked = !canAdmin || selected?.is_system;

  const changePerm = (module, level) => {
    if (permsLocked) return;
    setDraft((d) => ({ ...d, permissions: { ...d.permissions, [module]: level } }));
    setDirty(true);
  };
  const changeField = (field, value) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setDirty(true);
  };

  const save = async () => {
    if (!draft || !selected) return;
    try {
      const body = {};
      if (!namingLocked && draft.name !== selected.name) body.name = draft.name.trim();
      if (draft.description !== (selected.description || "")) body.description = draft.description;
      if (!permsLocked) body.permissions = draft.permissions;
      if (!Object.keys(body).length) { toast.info("Nothing to save"); return; }
      const r = await api.patch(`/roles/${selected.id}`, body);
      toast.success("Role updated");
      setDirty(false);
      setRoles((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    if (!selected || selected.is_system) return;
    if (!window.confirm(`Delete role "${selected.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/roles/${selected.id}`);
      toast.success("Role deleted");
      setSelectedId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="roles-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Masters</div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Roles & Permissions</h1>
          <p className="text-slate-500 text-sm mt-1">Define who can do what. Coarse-grained per module: <b>None · View · Edit · Admin</b>. Admin is always all-inclusive.</p>
        </div>
        {canAdmin && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-role-btn"><Plus className="w-4 h-4 mr-1.5" /> New role</Button>
            </DialogTrigger>
            <CreateRoleDialog meta={meta} onCreated={(r) => { setCreateOpen(false); setSelectedId(r.id); load(); }} onClose={() => setCreateOpen(false)} />
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Role list */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100 text-xs font-semibold uppercase tracking-wider text-slate-500">Roles</div>
          <ul>
            {roles.map((r) => (
              <li key={r.id}>
                <button onClick={() => setSelectedId(r.id)} data-testid={`role-row-${r.id}`}
                  className={`w-full text-left px-3 py-2.5 text-sm border-l-2 transition-colors duration-150 ${selectedId === r.id ? "bg-indigo-50 border-indigo-500" : "border-transparent hover:bg-slate-50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {r.name === meta.system_admin_role_name ? <Lock className="w-3.5 h-3.5 text-indigo-600 shrink-0" /> : r.is_system ? <ShieldCheck className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <span className="w-3.5 h-3.5 rounded-full bg-slate-200 shrink-0" />}
                      <span className="font-medium text-slate-900 truncate">{r.name}</span>
                    </div>
                    <span className="text-xs text-slate-500 shrink-0"><Users className="w-3 h-3 inline mr-0.5" />{r.user_count}</span>
                  </div>
                  {r.is_system && <div className="text-xs text-slate-400 mt-0.5">System role</div>}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Role detail */}
        {selected && draft ? (
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role name</Label>
                  <Input value={draft.name} onChange={(e) => changeField("name", e.target.value)} disabled={namingLocked} className="mt-1" data-testid="role-name-input" />
                  {namingLocked && <p className="text-xs text-slate-500 mt-1">System roles cannot be renamed.</p>}
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</Label>
                  <Textarea value={draft.description || ""} onChange={(e) => changeField("description", e.target.value)} disabled={!canAdmin} rows={2} className="mt-1" data-testid="role-desc-input" />
                </div>
              </div>
              <div className="flex flex-col gap-2 items-end">
                <Badge variant="outline" className="text-xs">{selected.user_count} user{selected.user_count === 1 ? "" : "s"}</Badge>
                {canAdmin && !selected.is_system && (
                  <Button variant="outline" size="sm" onClick={remove} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-role-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
                )}
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-baseline justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Permissions per module</Label>
                {isAdminRole && (
                  <span className="text-xs text-indigo-700 flex items-center gap-1"><Lock className="w-3 h-3" /> Admin role permissions are locked</span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {meta.modules.map((m) => (
                  <div key={m} className="border border-slate-200 rounded-md p-3" data-testid={`perm-card-${m}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-sm text-slate-900">{MODULE_LABEL[m] || m}</div>
                      <Badge variant="outline" className={LEVEL_STYLES[draft.permissions[m] || "none"]}>{LEVEL_LABEL[draft.permissions[m] || "none"]}</Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-1">
                      {meta.levels.map((lvl) => (
                        <button
                          key={lvl}
                          type="button"
                          disabled={permsLocked}
                          onClick={() => changePerm(m, lvl)}
                          data-testid={`perm-${m}-${lvl}`}
                          className={`text-xs px-2 py-1.5 rounded border transition-colors duration-150 ${(draft.permissions[m] || "none") === lvl ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"} ${permsLocked ? "opacity-60 cursor-not-allowed" : ""}`}>
                          {LEVEL_LABEL[lvl]}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-xs text-slate-500">
                Hierarchy: <b>None</b> &lt; <b>View</b> &lt; <b>Edit</b> &lt; <b>Admin</b>. Higher levels include everything below.
                For Leads, Bookings and Quotations, <b>Edit</b> means edit-your-own; <b>Admin</b> means edit anyone's.
              </div>
            </div>

            {canAdmin && (
              <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
                <Button variant="outline" onClick={() => { setDraft({ name: selected.name, description: selected.description || "", permissions: { ...selected.permissions } }); setDirty(false); }} disabled={!dirty}>Discard</Button>
                <Button onClick={save} disabled={!dirty} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-role-btn"><Save className="w-3.5 h-3.5 mr-1" /> Save changes</Button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400">
            <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-slate-300" /> Pick a role on the left to view or edit it.
          </div>
        )}
      </div>
    </div>
  );

  function CreateRoleDialog({ meta, onCreated, onClose }) {
    const [form, setForm] = useState({ name: "", description: "", permissions: permsFromMeta(meta.modules) });
    const submit = async (e) => {
      e.preventDefault();
      if (!form.name.trim()) { toast.error("Name required"); return; }
      try {
        const r = await api.post("/roles", form);
        toast.success(`Role "${r.data.name}" created`);
        onCreated?.(r.data);
      } catch (e2) { toast.error(formatApiError(e2)); }
    };
    return (
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New role</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Name *</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sales Manager" data-testid="new-role-name" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="new-role-desc" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Permissions</Label>
            <div className="mt-1 space-y-2">
              {meta.modules.map((m) => (
                <div key={m} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
                  <div className="flex-1 text-sm">{MODULE_LABEL[m] || m}</div>
                  <div className="flex gap-1">
                    {meta.levels.map((lvl) => (
                      <button key={lvl} type="button" onClick={() => setForm({ ...form, permissions: { ...form.permissions, [m]: lvl } })}
                        className={`text-xs px-2 py-1 rounded border ${form.permissions[m] === lvl ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                        {LEVEL_LABEL[lvl]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-new-role-btn">Create role</Button>
          </div>
        </form>
      </DialogContent>
    );
  }
}
